import clsx from "clsx";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { getRowsFromData } from "@/Washouts/helpers/getRowsFromData";
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";
// MUI
import { Grid, SxProps } from "@mui/material";
// Tables
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import CallActivityPerformanceWrapper from "@/Washouts/components/CallActivityPerformanceWrapper";
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import DepartmentalWashoutsTable from "@/Washouts/components/tables/Common/DepartmentalWashoutsTable";
import LongevityCard from "@/Washouts/components/tables/Common/LongevityCard";
// Types
import { BASE_TABLE_PROPS } from "@/Washouts/constants";
import { IWashoutModel } from "@/Washouts/types/commons";
import { IDepartmentalWashoutContent, IDepartmentalWashoutsTable, ITableInformation, SummaryRecord } from "@/Washouts/types/departmental";

const synchronizeTables = (table1: IDepartmentalWashoutsTable, table2: IDepartmentalWashoutsTable, iteration = 0) => {
	const table1Display = table1?.rows ?? [];
	const table2Display = table2?.rows ?? [];
	let emptyTable;
	if (table1.tableRef === "emptyTable") {
		emptyTable = table1;
	} else if (table2.tableRef === "emptyTable") {
		emptyTable = table2;
	}
	let newRowCount = 0;
	if (table1Display?.length !== table2Display?.length) {
		const minTable = table1Display.length < table2Display?.length ? table1Display : table2Display;
		const maxRows = Math.max(table1Display?.length, table2Display?.length);
		while (minTable?.length < maxRows - (emptyTable ? 1 : 0)) {
			minTable?.push?.({
				_id: `synchronize-washout-row-${iteration}-${newRowCount}`,
				emptyRow: true,
			});
			newRowCount += 1;
		}
	}
	return { table1, table2 };
};

const getTableInformationByReference = (tableRef: string) => {
	const options: Record<string, ITableInformation> = {
		bodyShopDepartment: {
			color: "gray",
			title: "Body Shop Department",
			tableRef,
		},
		serviceDepartment: {
			color: "yellow",
			title: "Service Department",
			tableRef,
		},
		partsDepartment: {
			color: "blue",
			title: "Parts Department",
			tableRef,
		},
		fixedOperationsCosts: {
			color: "pink",
			title: "Fixed Operations Cost",
			tableRef,
		},
	};
	return options?.[tableRef] || null;
};

const getTablesFromModel = (model: IDepartmentalWashoutContent["model"], positionWithoutSpaces = "") => {
	const tables: IDepartmentalWashoutsTable[] = [];
	const position = String(positionWithoutSpaces).toUpperCase();
	let orderToDisplay = ["serviceDepartment", "partsDepartment", "bodyShopDepartment"];
	if (position.includes("PARTS")) {
		orderToDisplay = ["partsDepartment", "serviceDepartment", "bodyShopDepartment"];
	} else if (position?.includes("BODYSHOP")) {
		orderToDisplay = ["bodyShopDepartment", "partsDepartment", "serviceDepartment"];
	} else if (position?.includes("SERVICE")) {
		orderToDisplay = ["serviceDepartment", "partsDepartment", "bodyShopDepartment"];
	}
	orderToDisplay.push("fixedOperationsCosts");
	orderToDisplay.forEach?.((key) => {
		if (and(model?.[key]?.totals, !model?.[key]?.hideInWashout)) {
			const tableToShow: IDepartmentalWashoutsTable = getTableInformationByReference(key);
			if (tableToShow) {
				tableToShow["rows"] = getRowsFromData(model?.[key]);
				tableToShow["totals"] = model?.[key]?.totals;
				tables.push(tableToShow);
			}
		}
	});
	const setsOfTables = [];
	let iteration = 0;
	for (let i = 0; i < tables.length; i += 2) {
		let addedEmptyTable = false;
		if (and(Boolean(tables[i]), !tables[i + 1])) {
			tables[i + 1] = {
				color: "none",
				title: "",
				tableRef: "emptyTable",
				rows: [],
				totals: {},
			};
			addedEmptyTable = true;
		}
		const synchronizedTables = synchronizeTables(tables[i], tables[i + 1], iteration);
		iteration += 1;
		tables[i] = synchronizedTables?.table1;
		tables[i + 1] = synchronizedTables?.table2;
		if (and(addedEmptyTable, Boolean(tables[i]?.totals))) {
			tables[i + 1]?.rows?.push({
				_id: "totals-row-" + iteration,
			});
			iteration += 1;
		}
		setsOfTables.push({
			table1: tables[i],
			table2: tables[i + 1],
		});
	}
	return setsOfTables;
};

const getGrossColumnTitle = (summary: SummaryRecord[] = []) => {
	const isVSG = summary.some((entry) => String(entry?.item)?.toUpperCase()?.includes("FIXED OPERATIONS"));
	return isVSG ? "Volume Gross (VSG)" : "Volume Gross (VG)";
};

function PrepareWrappedTable(
	props: Readonly<{
		table: IDepartmentalWashoutsTable;
		defaultSx: SxProps;
		isLoading: boolean;
		model: IDepartmentalWashoutContent["model"];
	}>
) {
	if (!props.table) {
		return null;
	}

	return (
		<Grid item key={uuidv4()} className="w-[646px]">
			<DepartmentalWashoutsTable
				{...props.table}
				loading={Boolean(props.isLoading)}
				dataPresent={Boolean(props.model)}
				showPVR={false}
			/>
		</Grid>
	);
}

function DynamicTablesSection(
	props: Readonly<{
		departmentalTablesToShow: { table1: IDepartmentalWashoutsTable; table2: IDepartmentalWashoutsTable }[];
		defaultSx: SxProps;
		isLoading: boolean;
		model: IDepartmentalWashoutContent["model"];
	}>
) {
	if (props.isLoading) {
		return (
			<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
				<Grid className="w-[646px]">
					<TableLoader bigTitle toolbar buttons={2} content rows={8} />
				</Grid>
				<Grid className="w-[646px]">
					<TableLoader bigTitle toolbar buttons={2} content rows={8} />
				</Grid>
			</Grid>
		);
	}

	return (
		<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px]">
			{props.departmentalTablesToShow?.map?.((tables) => (
				<>
					<PrepareWrappedTable table={tables.table1} {...props} />
					<PrepareWrappedTable table={tables.table2} {...props} />
				</>
			))}
		</Grid>
	);
}

function CompensationWrapper({
	model = null,
	selected = null,
	overrideCompensation = false,
	isLoading = false,
	handleSummaryChange = () => {},
}: Readonly<IDepartmentalWashoutContent>) {
	const isEditable = useIsWashoutEditable({
		cycleStatus: selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: overrideCompensation,
	});

	return (
		<Compensation
			data={model?.summary?.summary ?? []}
			addedData={model?.addedCompensations ?? []}
			tooltip
			editable={isEditable}
			columns={[
				{
					field: "gross",
					headerName: getGrossColumnTitle(model?.summary?.summary ?? []),
					align: "right",
					headerAlign: "right",
					cellsType: "dollar",
					width: 175,
				},
				{
					field: "tier",
					headerName: "Eff. Inc. Rt.",
					align: "right",
					headerAlign: "right",
					cellsType: "percentage",
					round: 2,
					flex: 1,
				},
			]}
			itemsDefinitions={[
				{ item: "Chargebacks Gross Commission", type: "percentage" },
				{ item: "Variable Gross Commissions", type: "percentage" },
				{ item: "Miami Stores USCER-Bonus", type: "percentage" },
				{ item: "Inbound Missed Calls", type: "percentage" },
			]}
			dataPresent={Boolean(model)}
			loading={isLoading}
			onActionUpdate={handleSummaryChange}
		/>
	);
}

export default function DepartmentalWashoutContent(props: Readonly<IDepartmentalWashoutContent>) {
	const {
		model = null,
		selected = null,
		isLoading = false,
		hideEmployeeMeta = false,
		overrideCompensation = false,
		handleSummaryChange = () => {},
		positionWithoutSpaces = "",
		disableBoxShadow = false,
	} = props;
	const [departmentalTablesToShow, setDepartmentalTablesToShow] = useState<
		{ table1: IDepartmentalWashoutsTable; table2: IDepartmentalWashoutsTable }[]
	>([]);

	useEffect(() => {
		if (model) {
			setDepartmentalTablesToShow(getTablesFromModel(model, positionWithoutSpaces));
		} else {
			setDepartmentalTablesToShow([]);
		}
	}, [model, positionWithoutSpaces]);

	const defaultSx: SxProps = {
		padding: Boolean(model) || isLoading ? "5px 30px 50px 30px" : "0px",
	};
	const callActivityPresent = and(Boolean(model), Boolean(model?.callActivityPerformance));

	return (
		<Grid
			container
			direction="column"
			className={clsx({
				"pb-[30px]": true,
				"shadow-white-section": !disableBoxShadow,
				"min-h-[620px]": !model && isLoading,
				hidden: !model && !isLoading,
			})}
			rowSpacing={0}
		>
			<EmployeeMetaSection
				{...props.employeeMetaProps}
				model={model}
				hidden={hideEmployeeMeta}
				loading={isLoading}
				secondaryColumns={["DAYS_OUT"]}
			/>

			<DynamicTablesSection
				model={model}
				departmentalTablesToShow={departmentalTablesToShow}
				defaultSx={defaultSx}
				isLoading={isLoading}
			/>

			<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px] justify-end">
				<Grid item className="w-[646px]">
					<LongevityCard
						data={[{ ...model?.longevity }]}
						loading={isLoading}
						minRows={1}
						columnsOverride={[
							{ field: "hiredDate", width: -1 },
							{ field: "years", width: -1 },
							{ field: "compensation", width: -1 },
						]}
					/>
				</Grid>

				{callActivityPresent && (
					<Grid item className="w-[646px]">
						<CallActivityPerformanceWrapper
							model={model as IWashoutModel}
							editable={or(selected?.status === "ACTIVE", selected?.status === "CLOSING")}
							dataPresent={callActivityPresent}
							loading={isLoading}
							onActionUpdate={handleSummaryChange}
							tableProps={{ ...BASE_TABLE_PROPS }}
						/>
					</Grid>
				)}

				<Grid item className="w-[646px]">
					<CompensationWrapper
						model={model}
						selected={selected}
						overrideCompensation={overrideCompensation}
						isLoading={isLoading}
						handleSummaryChange={handleSummaryChange}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
